import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faBolt, faPaperPlane } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Information } from "@/components/Information/Information";

import { CustomColumnTypes } from "@/enum/list.enum";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { TestMessageModal } from "@/pages/Private/pages/Messages/components/AiSnippets/TestMessageModal";
import { extractInsertedColumnIds } from "@/pages/Private/pages/Messages/components/AiSnippets/utils/snippetTokens";

import type { List } from "../../../schema/list";

import type { CampaignNodeTypes } from "../config/node-types";

const MESSAGE_NODE_TYPES: CampaignNodeTypes[] = [
	"emailNode",
	"linkedinNode",
	"linkedinInviteNode",
	"linkedinInmailNode",
];

export interface TestMessageBannerProps {
	nodeType: CampaignNodeTypes;
	message?: string;
	premiumMessage?: string;
	listData?: List;
	onSendTest: (variant?: "standard" | "premium") => void;
	/** When omitted the "Create AI snippets" button renders disabled. */
	onCreateAiSnippets?: () => void;
}

/**
 * TestMessageBanner — action banner rendered below the node-panel editors.
 *
 * Bundles everything around verifying a message before it goes out:
 * sending it as a test, resolving its AI snippets for the current lead,
 * and creating new AI snippets.
 */
export const TestMessageBanner: FunctionComponent<TestMessageBannerProps> = ({
	nodeType,
	message,
	premiumMessage,
	listData,
	onSendTest,
	onCreateAiSnippets,
}) => {
	const { t } = useTranslation();
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);

	const [showTestModal, setShowTestModal] = useState<boolean>(false);
	const currentLead = inbox?.[currentLeadIndex];
	// A test message is rendered for a lead; an empty list has none since PREC-4790, so the send
	// would open a modal that can never send. Disable it and say why (review r1 S3).
	const hasNoLead = !currentLead;

	// The snippet test closes for good when the lead goes away; otherwise it would reopen on its own
	// with the next lead and start another prompt run (review r2 S1).
	useEffect(() => {
		if (hasNoLead) {
			setShowTestModal(false);
		}
	}, [hasNoLead]);

	const hasAiSnippetInMessage = useMemo(() => {
		const referencedColumnIds = extractInsertedColumnIds([message ?? "", premiumMessage ?? ""]);

		return (listData?.columns ?? []).some(
			(c) =>
				c.type === CustomColumnTypes.AI_SNIPPET &&
				c.id != null &&
				referencedColumnIds.includes(c.id)
		);
	}, [message, premiumMessage, listData?.columns]);

	if (!MESSAGE_NODE_TYPES.includes(nodeType)) {
		return null;
	}

	const prospectName = currentLead?.prospect?.name;

	return (
		<div className="mt-4 flex w-full flex-col gap-2.5 rounded-lg border border-blue-action-200 bg-blue-action-200/50 p-3">
			<div className="flex flex-row items-center gap-2.5">
				<div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border border-blue-action-200 bg-white text-blue-action">
					<Icon className="w-3.5 h-3.5" icon={faBolt} />
				</div>
				<div className="text-xs font-semibold text-text">{t("campaign.testBanner.title")}</div>
				<Information information={t("campaign.testBanner.info")} />
			</div>

			<div className="flex flex-row flex-wrap items-center gap-1.5">
				{nodeType === "linkedinInviteNode" ? (
					<>
						{!!message && (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon className="w-3 h-3 mr-1.5" icon={faPaperPlane} />}
								size={ButtonSize.XS}
								title={t("campaign.testBanner.sendAsTestStandard")}
								disabled={hasNoLead}
								onClick={() => onSendTest("standard")}
							/>
						)}
						{!!premiumMessage && (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon className="w-3 h-3 mr-1.5" icon={faPaperPlane} />}
								size={ButtonSize.XS}
								title={t("campaign.testBanner.sendAsTestPremium")}
								disabled={hasNoLead}
								onClick={() => onSendTest("premium")}
							/>
						)}
					</>
				) : (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="w-3 h-3 mr-1.5" icon={faPaperPlane} />}
						size={ButtonSize.XS}
						title={t("campaign.testBanner.sendAsTest")}
						disabled={hasNoLead}
						onClick={() => onSendTest()}
					/>
				)}

				{/* The snippet test resolves for the current lead; an empty list has none (PREC-4790). */}
				{hasAiSnippetInMessage && !!currentLead && (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						title={t("aiSnippet.section.testForLead", { name: prospectName ?? "this lead" })}
						onClick={() => setShowTestModal(true)}
					/>
				)}
			</div>

			{hasNoLead && (
				<p className="text-xs text-text-secondary">{t("campaign.testBanner.noLead")}</p>
			)}

			<TestMessageModal
				isOpen={showTestModal && !hasNoLead}
				message={message ?? ""}
				premiumMessage={premiumMessage}
				onClose={() => setShowTestModal(false)}
			/>
		</div>
	);
};
